import {
  Alert,
  Box,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import Button from '@mui/material/Button';
import { Close as CloseIcon } from '@mui/icons-material';
import React, { useContext, useState } from 'react';
import { I_S3Credentials } from '../../../interfaces/S3Types';
import { I_Installation } from '../../../interfaces/InstallationTypes';
import { InstallationsContext } from '../../../contexts/InstallationsContextProvider';
import { UserContext } from '../../../contexts/userContext';
import { UserType } from '../../../interfaces/UserTypes';
import { ProductIdContext } from '../../../contexts/ProductIdContextProvider';

import { useLocation, useNavigate } from 'react-router-dom';

interface InformationProps {
  values: I_Installation;
  s3Credentials: I_S3Credentials;
  type?: string;
}

function Information(props: InformationProps) {
  if (props.values === null) {
    return null;
  }

  const context = useContext(UserContext);
  const { currentUser } = context;
  const theme = useTheme();
  const [formValues, setFormValues] = useState(props.values);
  const requiredFields = ['name', 'region', 'location', 'country'];
  const installationContext = useContext(InstallationsContext);
  const { product, setProduct } = useContext(ProductIdContext);
  const {
    updateInstallation,
    loading,
    setLoading,
    error,
    setError,
    updated,
    setUpdated,
    deleteInstallation
  } = installationContext;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
  };
  const handleSubmit = () => {
    setLoading(true);
    setError(false);
    updateInstallation(formValues, props.type);
  };

  const areRequiredFieldsFilled = () => {
    for (const field of requiredFields) {
      if (!formValues[field]) {
        return false;
      }
    }
    return true;
  };

  const [openModalDeleteInstallation, setOpenModalDeleteInstallation] =
    useState(false);

  const handleDelete = () => {
    setLoading(true);
    setError(false);
    setOpenModalDeleteInstallation(true);
  };

  const location = useLocation();
  const navigate = useNavigate();

  const deleteInstallationModalHandle = () => {
    setOpenModalDeleteInstallation(false);
    deleteInstallation(formValues, props.type);
    setLoading(false);

    const pathSegments = location.pathname.split('/'); // e.g. ['', 'sodiohome_installations', 'list', 'installation', '123', 'information']

    const base = `/${pathSegments[1]}`;
    const isListView = pathSegments.includes('list');
    const isTreeView = pathSegments.includes('tree');

    if (isListView) {
      navigate(`${base}/list/`);
    } else if (isTreeView) {
      navigate(`${base}/tree/`);
    } else {
      // fallback
      navigate(`${base}/list/`);
    }
  };

  const deleteInstallationModalHandleCancel = () => {
    setOpenModalDeleteInstallation(false);
    setLoading(false);
  };

  return (
    <>
      {openModalDeleteInstallation && (
        <Modal
          open={openModalDeleteInstallation}
          onClose={deleteInstallationModalHandleCancel}
          aria-labelledby="error-modal"
          aria-describedby="error-modal-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 350,
              bgcolor: 'background.paper',
              borderRadius: 4,
              boxShadow: 24,
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="body1"
              gutterBottom
              sx={{ fontWeight: 'bold' }}
            >
              Do you want to delete this installation?
            </Typography>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: 10
              }}
            >
              <Button
                sx={{
                  marginTop: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={deleteInstallationModalHandle}
              >
                Delete
              </Button>
              <Button
                sx={{
                  marginTop: 2,
                  marginLeft: 2,
                  textTransform: 'none',
                  bgcolor: '#ffc04d',
                  color: '#111111',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
                onClick={deleteInstallationModalHandleCancel}
              >
                Cancel
              </Button>
            </div>
          </Box>
        </Modal>
      )}
      <Container maxWidth="xl">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
          spacing={3}
        >
          <Grid item xs={12} md={12}>
            <CardContent>
              <Box
                component="form"
                sx={{
                  '& .MuiTextField-root': { m: 1, width: '50ch' }
                }}
                noValidate
                autoComplete="off"
              >
                <div>
                  <TextField
                    label={<FormattedMessage id="name" defaultMessage="name" />}
                    name="name"
                    value={formValues.name}
                    onChange={handleChange}
                    fullWidth
                    required
                    error={formValues.name === ''}
                  />
                </div>
                <div>
                  <TextField
                    label={
                      <FormattedMessage id="region" defaultMessage="Region" />
                    }
                    name="region"
                    value={formValues.region}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    required
                    error={formValues.region === ''}
                  />
                </div>
                <div>
                  <TextField
                    label={
                      <FormattedMessage
                        id="location"
                        defaultMessage="Location"
                      />
                    }
                    name="location"
                    value={formValues.location}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    required
                    error={formValues.location === ''}
                  />
                </div>
                <div>
                  <TextField
                    label={
                      <FormattedMessage id="country" defaultMessage="Country" />
                    }
                    name="country"
                    value={formValues.country}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    required
                    error={formValues.country === ''}
                  />
                </div>
                <div>
                  <TextField
                    label={
                      <FormattedMessage
                        id="orderNumbers"
                        defaultMessage="Order Numbers"
                      />
                    }
                    name="orderNumbers"
                    value={formValues.orderNumbers}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                  />
                </div>
                <div>
                  <TextField
                    label={
                      <FormattedMessage
                        id="installation_name"
                        defaultMessage="Installation Name"
                      />
                    }
                    name="installationName"
                    value={formValues.installationName}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                  />
                </div>

                <div>
                  <TextField
                    label={
                      <FormattedMessage
                        id="information"
                        defaultMessage="Information"
                      />
                    }
                    name="information"
                    value={formValues.information}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={6} // 👈 Makes it visually bigger
                    maxRows={12} // 👈 Optional max height before scroll
                    inputProps={{
                      style: {
                        fontFamily: 'monospace' // optional: makes tabs/formatting more clear
                      }
                    }}
                  />
                </div>

                {currentUser.userType == UserType.admin && (
                  <>
                    <div>
                      <TextField
                        label="Vpn IP"
                        name="VpnIp"
                        value={formValues.vpnIp}
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div>
                      <TextField
                        label="S3 Bucket Name"
                        name="s3bucketname"
                        value={
                          product === 0 || product == 3
                            ? formValues.s3BucketId +
                              '-3e5b3069-214a-43ee-8d85-57d72000c19d'
                            : formValues.s3BucketId +
                              '-e7b9a240-3c5d-4d2e-a019-6d8b1f7b73fa'
                        }
                        variant="outlined"
                        fullWidth
                      />
                    </div>
                    <div>
                      <TextField
                        label="S3 Write Key"
                        name="s3writekey"
                        value={formValues.s3WriteKey}
                        variant="outlined"
                        fullWidth
                      />
                    </div>

                    <div>
                      <TextField
                        label="S3 Write Secret Key"
                        name="s3writesecretkey"
                        value={formValues.s3WriteSecret}
                        variant="outlined"
                        fullWidth
                      />
                    </div>
                  </>
                )}

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: 10
                  }}
                >
                  {currentUser.userType == UserType.admin && (
                    <Button
                      variant="contained"
                      onClick={handleDelete}
                      sx={{
                        marginLeft: '10px'
                      }}
                    >
                      <FormattedMessage
                        id="deleteInstallation"
                        defaultMessage="Delete Installation"
                      />
                    </Button>
                  )}
                  {currentUser.userType == UserType.admin && (
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      sx={{
                        marginLeft: '10px'
                      }}
                      disabled={!areRequiredFieldsFilled()}
                    >
                      <FormattedMessage
                        id="applyChanges"
                        defaultMessage="Apply Changes"
                      />
                    </Button>
                  )}

                  {loading && (
                    <CircularProgress
                      sx={{
                        color: theme.palette.primary.main,
                        marginLeft: '20px'
                      }}
                    />
                  )}
                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        ml: 1,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <FormattedMessage
                        id="errorOccured"
                        defaultMessage="An error has occurred"
                      />
                      <IconButton
                        color="inherit"
                        size="small"
                        onClick={() => setError(false)}
                        sx={{ marginLeft: '4px' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Alert>
                  )}
                  {updated && (
                    <Alert
                      severity="success"
                      sx={{
                        ml: 1,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <FormattedMessage
                        id="successfullyUpdated"
                        defaultMessage="Successfully updated"
                      />

                      <IconButton
                        color="inherit"
                        size="small"
                        onClick={() => setUpdated(false)} // Set error state to false on click
                        sx={{ marginLeft: '4px' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Alert>
                  )}
                </div>
              </Box>
            </CardContent>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default Information;
